import {
  Component, ElementRef,
  Input, OnChanges,
  OnInit, Optional, PipeTransform, Self, SimpleChanges,
  ViewChild,
} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, startWith, tap} from 'rxjs/operators';
import {LookupModel} from '../../api/models';
import { MatFormFieldControl, MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import {MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import { UntypedFormControl, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import { MatChipGrid, MatChipInputEvent, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import {EmailPipe} from '../../core/pipes/email.pipe';
import {MatBaseInputComponent} from '../mat-base-input.component';
import {PhoneNumberPipe} from '../../core/pipes/phone-number.pipe';
import { MatFormFieldFix } from '../mat-form-field/mat-form-field-fix';
import { NgFor, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { WrappingPipe } from '../../core/pipes/wrapping.pipe';

@Component({
    selector: 'app-mat-multiselect',
    template: `
    <mat-form-field appearance="outline" class="regular-form-field multiselect" [hintLabel]="hintLabel"
                    (focusin)="onFocusIn($event)"
                    (focusout)="onFocusOut($event)">
      <mat-label>{{label}}</mat-label>
      <mat-chip-grid #chipList [disabled]="disabled || submitted">
        <mat-chip-row *ngFor="let item of selectedItems" [editable]="!disabled && !submitted" [removable]="!disabled && !submitted"
                  (removed)="remove(item)">
          <div class="recipient-title">
            {{ getItemName(item) | wrappingPipe:displayPipe }}
          </div>
          <mat-icon matChipRemove *ngIf="!disabled && !submitted">cancel</mat-icon>
        </mat-chip-row>

        <input class="autocomplete" type="text" #inputTrigger [disabled]="disabled || submitted || (limit != -1 && limit <= selectedItems.length)"
               matInput
               #trigger="matAutocompleteTrigger"
               [matAutocomplete]="autoComplete"
               [formControl]="itemControl"
               [placeholder]="placeholder"
               [matChipInputFor]="chipList"
               [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
               (matChipInputTokenEnd)="addRecipient($event)"
               (click)="openSelectOptions($event, trigger)"
        />
      </mat-chip-grid>

      <mat-autocomplete #autoComplete="matAutocomplete" (optionSelected)="optionSelected($event)">
        <mat-option *ngFor="let item of items" [value]="item[valueField]" [disabled]="item.text.indexOf('invalid') > -1">
          {{ item.text | wrappingPipe:lookupDisplayPipe }}
        </mat-option>
      </mat-autocomplete>
      <button mat-icon-button matSuffix type="button" *ngIf="suffixIcon" (click)="onSuffixClicked.emit($event)"
              [matTooltip]="suffixTooltip">
        <mat-icon matSuffix>{{suffixIcon}}</mat-icon>
      </button>
      <mat-error *ngIf="hasErrors()">{{firstError()}}</mat-error>
    </mat-form-field>
  `,
    styleUrls: ['./mat-multiselect.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: MatMultiselectComponent }
    ],
    standalone: true,
    imports: [MatFormField, MatFormFieldFix, MatLabel, MatChipGrid, NgFor, MatChipRow, NgIf, MatIcon, MatChipRemove, MatInput, MatAutocompleteTrigger, FormsModule, MatChipInput, ReactiveFormsModule, MatAutocomplete, MatOption, MatIconButton, MatSuffix, MatTooltip, MatError, WrappingPipe]
})
export class MatMultiselectComponent extends MatBaseInputComponent implements OnInit, OnChanges {

  separatorKeysCodes: number[] = [ENTER, COMMA];

  @ViewChild('autoComplete') autoComplete: MatAutocomplete;
  @ViewChild('trigger') trigger: MatAutocompleteTrigger;
  @ViewChild('chipList') chipList: MatChipGrid;
  @Input() autoCompleteValues: LookupModel[] = [];
  @Input() excludeValues: LookupModel[] = [];
  @Input() validator: 'none' | 'mail' | 'phoneNumber' = 'none';
  @Input() valueField = 'id';
  @Input() allowCustomValue = false;
  @Input() includeSelected = false;
  @Input() limit = -1;
  @Input() displayPipe: PipeTransform;
  @Input() lookupDisplayPipe: PipeTransform;

  phoneNumberPipe = new PhoneNumberPipe();

  @Input() set value(value: any) {
    if (value) {
      this.selectedItems = value;
    }
    this.stateChanges.next();
  }

  get value() {
    return this.selectedItems;
  }

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    public elementRef: ElementRef<HTMLElement>,
  ) {
    super(ngControl, elementRef);
  }

  @ViewChild('inputTrigger') inputTrigger: ElementRef<HTMLInputElement>;
  itemControl = new UntypedFormControl();
  items: LookupModel[] = [];
  selectedItems: string[] = [];
  lastFilter = '';

  ngOnInit() {
    this.itemControl.valueChanges.pipe(
      startWith<string | LookupModel[]>(''),
      map(value => typeof value === 'string' ? value : this.lastFilter),
      map(searchText => {
        return this.filter(searchText)
          .pipe(
            tap((data) => {
              console.log('on search');
              if (searchText) {
                const newItemText = searchText.trim();
                if (newItemText) {
                  if (this.validator === 'mail') {
                    const isValidEmail = EmailPipe.isValidEmail(newItemText);
                    if (isValidEmail) {
                      data.splice(0, 0, {
                        id: newItemText,
                        text: newItemText,
                      });
                    } else {
                      data.splice(0, 0, {
                        id: newItemText,
                        text: `${newItemText} (invalid)`,
                      });
                    }
                  } else if (this.validator === 'phoneNumber') {
                    const phoneNumberText = this.phoneNumberPipe.transform(searchText, true);
                    const isValidPhoneNumber = !phoneNumberText?.includes('invalid');
                    if (isValidPhoneNumber) {
                      data.splice(0, 0, {
                        id: phoneNumberText,
                        text: phoneNumberText,
                      });
                    } else {
                      data.splice(0, 0, {
                        id: phoneNumberText,
                        text: phoneNumberText,
                      });
                    }
                  } else if (this.allowCustomValue) {
                    data.splice(0, 0, {
                      id: newItemText,
                      text: newItemText,
                    });
                  }
                }
              }
            })
          );
      })
    ).subscribe((data => {
      data.subscribe((items) => this.items = items);
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    this.clearInput();
  }

  remove(item: string): void {
    const index = this.selectedItems.findIndex(value => value === item);
    if (index >= 0) {
      this.selectedItems.splice(index, 1);
      this.propagateChange(this.selectedItems);
      this.clearInput();
    }
  }

  getItemName(item: string) {
    if (this.autoCompleteValues?.length > 0) {
      const lookupItem = this.autoCompleteValues.find(x => x.id === item);
      if (lookupItem) {
        return lookupItem.text;
      }
    }

    return item;
  }

  filter(searchText: string): Observable<LookupModel[]> {
    return of((this.autoCompleteValues || []).filter(x => {
      const text = (x.text || '').toLowerCase();
      const search = (searchText || '').toLowerCase();

      if (!this.includeSelected) {
        return this.selectedItems.findIndex(e => e === x.id) === -1
          && (this.excludeValues || []).findIndex(e => e.id === x.id) === -1
          && text.indexOf(search) > -1;
      }
      return (this.excludeValues || []).findIndex(e => e.id === x.id) === -1 && text.indexOf(search) > -1;
    }));
  }

  addRecipient($event: MatChipInputEvent) {
    console.log($event);

    const possibleEmail = $event.value.trim();

    if (!possibleEmail) {
      return undefined;
    }

    if (this.validator === 'mail') {
      const isValidEmail = EmailPipe.isValidEmail(possibleEmail);
      if (isValidEmail) {
        this.selectedItems.push(possibleEmail);
        this.propagateChange(this.selectedItems);
        this.clearInput();
      } else {
        return undefined;
      }
    } else if (this.allowCustomValue) {
      this.selectedItems.push(possibleEmail);
      this.propagateChange(this.selectedItems);
      this.clearInput();
    }

    return undefined;
  }

  optionSelected($event: MatAutocompleteSelectedEvent) {
    console.log($event);
    const option: string = $event.option.value;
    const i = this.selectedItems.findIndex(value => value === option);

    if (i === -1) {
      this.selectedItems.push(option);
      this.propagateChange(this.selectedItems);
    }

    this.clearInput();
  }

  hasErrors() {
    let showErrors = false;
    if (!this.formControl) {
      this.errorState = showErrors;
      return showErrors;
    }

    showErrors = (this.formControl.invalid && (this.formControl.dirty || this.formControl.touched))
      || (this.submitted && this.formControl.invalid);

    this.errorState = showErrors;
    if (this.chipList) {
      this.chipList.errorState = showErrors;
    }

    return showErrors;
  }

  firstError(): string {
    const message = super.firstError();
    if (message) {
      this.hasErrors();
    } else {
      this.errorState = false;
    }
    return message;
  }

  clearInput() {
    if (this.inputTrigger?.nativeElement) {
      this.inputTrigger.nativeElement.value = '';
    }

    this.itemControl.setValue(null);
  }

  openSelectOptions($event: MouseEvent, trigger: MatAutocompleteTrigger) {
    $event.stopPropagation();
    trigger.openPanel();
  }
}
